import { apiClient } from './api-client';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

export const pushNotificationService = {
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  },

  async getPermission(): Promise<NotificationPermission> {
    return Notification.permission;
  },

  async requestPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
  },

  async register(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isSupported()) return null;
    try {
      return await navigator.serviceWorker.register('/sw.js');
    } catch {
      return null;
    }
  },

  async getVapidPublicKey(): Promise<string | null> {
    try {
      const res = await apiClient.get<{ vapidPublicKey: string | null }>('/alerts/push-subscription/status');
      return res.vapidPublicKey;
    } catch {
      return null;
    }
  },

  async isSubscribed(): Promise<boolean> {
    try {
      const res = await apiClient.get<{ active: boolean }>('/alerts/push-subscription/status');
      return res.active;
    } catch {
      return false;
    }
  },

  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    const permission = await this.requestPermission();
    if (permission !== 'granted') return false;

    const vapidKey = await this.getVapidPublicKey();
    if (!vapidKey) return false;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const json = sub.toJSON();
    await apiClient.post('/alerts/push-subscription', {
      endpoint: sub.endpoint,
      keys: {
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      },
    });
    return true;
  },

  async unsubscribe(): Promise<void> {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiClient.delete('/alerts/push-subscription', { data: { endpoint: sub.endpoint } });
      await sub.unsubscribe();
    }
  },
};
